/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { DraggableDirective } from "../../directives/draggable.directive";
import {Component, OnInit, ViewChild, Input, OnDestroy, ChangeDetectorRef} from '@angular/core';
import {ImageService} from "../../services/image.service";
import {LocateFilters} from "../../models/locateFilters";
import {LocateFilter} from "../../models/locateFilter";
import {CookieService} from "ngx-cookie-service";
import Filter = createjs.Filter;
import {TabType} from "./tabType";
import {ParameterData} from "../../models/ParameterData";
import {PropertyDescription} from "../../models/propertyDescription";

declare var $: any;


/**
 * Displays a popup which allows a user to create a new filter or edit an existing filter.
 *
 * There are 3 types of filters: Location, People and Asset filters. Set the parameter filterType to the type of filter to edit/create.
 * Set the filters to a LocateFilters object type which contains all the filters. This will be updated by the popup.
 * Set the properties to an array of properties which describe each property that may be selected in that filter type.
 *
 * Example:
 *
 * @example
 *     <app-filter-popup #filterDialog
 *          [filterName]="filterName"
 *          [filterType]="mSelectedTabType"
 *          [filters]="locateFilters"
 *          [properties]="propertiesForSelectedTabType()">
 *     </app-filter-popup>
 */
@Component({
  selector: 'app-filter-popup',
  templateUrl: './filter-popup-dialog.component.html'
})
export class FilterPopupDialogComponent implements OnInit, OnDestroy {
  /** Unique ID to give the popup. Parameter only necessary if more than 1 filter dialog on screen at same time */
  @Input()
  dialogId = "FILTER_DIALOG";

  /** Name type of the filter. This appears in the header of the popup. Note this is not the same as a filter name,
   * which the user enters in the popup. */
  @Input()
  filterName: String;

  /** Type of the filter. One of LOCATIONS, PEOPLE or ASSET */
  @Input()
  filterType: String;

  /** A LocateFilters object which holds the data for all the locate filters */
  @Input()
  filters: LocateFilters;

  @Input()
  popupInModal = false;

  /** Describes all the properties that may be selected inside this popup. Includes the names and property types */
  @Input()
  properties: PropertyDescription[];

  /** Current filter this popup is editing, viewing or creating */
  filter: LocateFilter;

  //Draggable Directive added to that the popup is draggable.  Referenced so that it can be reset when the popup is hidden
  @ViewChild(DraggableDirective) mDraggableDirective;

  @ViewChild('multiValueWidget') multiValueWidget;

  filterData = [ ];

  /** Number of days before the filter cookie expires */
  NUMBER_DAYS_FILTER_COOKIE_EXPIRATION = 90;

  constructor(
    private mImageService: ImageService,
    private cookieService: CookieService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    if (!this.filters) {
      // If filters do not exist yet, create skeleton so we can add new filters
      this.filters = new LocateFilters();
      this.filters.locationFilters = [];
      this.filters.peopleFilters = [];
      this.filters.assetFilters = [];
    }
    this.filter = new LocateFilter();
  }

  ngOnDestroy(): void {
  }

  /** Show the filter dialog */
  public show(): void {
    this.filter = new LocateFilter();
    console.log("Removing all filter Data...");
    console.log("BEFORE filterData.length = " + this.filterData.length);
    this.filterData = [];

    //this.filterData.splice(0, this.filterData.length);
    console.log("AFTER filterData.length = " + this.filterData.length);

    this.changeDetectorRef.detectChanges();

    $("#" + this.dialogId).show();
  }

  public hide(): void {
    $("#" + this.dialogId).hide();
    this.mDraggableDirective.reset();
  }

  getIdForDialogHeader(): string {
    return this.dialogId + "_header";
  }

  /** a new dialog is one that has not been saved */
  isNewDialog(): boolean {
    return !this.filter.saved;
  }

  /** an edit dialog is one that has been saved before */
  isEditDialog(): boolean {
    return this.filter.saved;
  }

  /** Adds or updates the current filter to the list of filters */
  createOrUpdateClicked(): void {
    console.log("createOrUpdateFilter Clicked");
    console.log("filter name=" + this.filter.name + ", connection= " + this.filter.connection);

    // Add/update the parameters to this filter
    const dataValueAsObject = this.multiValueWidget.getDataValueAsObject();
    const tempParameters = [];
    for(const filterData of dataValueAsObject) {
      const parameters = new ParameterData();
      parameters.name = filterData.name;
      parameters.comparison = filterData.comparison;
      parameters.value = filterData.value;
      if (!this.filter.parameters) {
        this.filter.parameters = [];
      }
      tempParameters.push(parameters);
      console.log("Adding/updating filter parameters: " + parameters.name + " " + parameters.comparison + " " + parameters.value);
    }

    this.filter.parameters = tempParameters;

    // Add this filter to the filters (if it's a new filter)
    if (!this.filter.saved) {
      this.filter.saved = true;
      switch (this.filterType) {
        case TabType.LOCATIONS :
          this.filters.locationFilters.push(this.filter);
          break;
        case TabType.ASSETS :
          this.filters.assetFilters.push(this.filter);
          break;
        case TabType.PEOPLE :
          this.filters.peopleFilters.push(this.filter);
          break;
        default:
      }
    }

    this.filter = new LocateFilter();

    // Store this new filter to the cookies (90 day expiration date)
    console.log("storing locate cookie");
    const expireDate = new Date();
    expireDate.setDate(expireDate.getDate() + this.NUMBER_DAYS_FILTER_COOKIE_EXPIRATION);
    this.cookieService.set('locate-filters', JSON.stringify(this.filters), expireDate);
    this.hide();
  }

  cancelClicked(): void {
    this.filter = new LocateFilter();
    this.hide();
  }

  /** Delete the filter currently viewing */
  deleteClicked(): void {
    // TODO: Finish the filter delete
    //if (this.filterName == "ASSET") {
    //  this.filter = this.filters.peopleFilters.find(option => option.name == name);
    //}
  }

  selectPeopleFilter(name: string): void {
    console.log("selectPeopleFilter");
    this.filter = this.filters.peopleFilters.find(option => option.name == name);
    this.updateFilterData();
  }

  selectAssetFilter(name: string): void {
    console.log("selectAssetFilter");
    this.filter = this.filters.assetFilters.find( option => option.name == name);
    this.updateFilterData();
  }

  /** select a location filter to edit */
  selectLocationFilter(name: string): void {
    console.log("selectLocationFilter('" + name + ')');
    this.filter = this.filters.locationFilters.find( option => option.name == name);
    this.updateFilterData();
  }

  private updateFilterData(): void {
    // Update filterData depending on this filter
    if (!this.filter.parameters) {
      this.filter.parameters = [];
    }

    const filterParameters = Object.create(this.filter.parameters);
    this.multiValueWidget.setDataValueAsObject(filterParameters);
  }


  filterConnectionChosen(connection: string): void {
    this.filter.connection = connection;
  }

}
